import { useState, useEffect, useCallback, useRef } from 'react';
import { apiCall, getApiUrl } from './config';

// Fetch JSON from an authenticated endpoint. `deps` re-run the request.
// `refreshMs` re-fetches silently on an interval and when the tab regains focus,
// so numbers update without a loading flash.
export function useApi(endpoint, { deps = [], enabled = true, refreshMs = 0 } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!endpoint) return;
    try {
      if (!silent) setLoading(true);
      const response = await apiCall(endpoint);
      if (!response.ok) throw new Error(`Request failed (${response.status})`);
      setData(await response.json());
      setError(null);
    } catch (err) {
      // A failed background refresh keeps the data already on screen
      if (!silent) setError(err.message || 'Request failed');
    } finally {
      if (!silent) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, ...deps]);

  useEffect(() => {
    if (enabled) load();
  }, [load, enabled]);

  useEffect(() => {
    if (!enabled || !refreshMs) return undefined;
    const refresh = () => { if (document.visibilityState === 'visible') load({ silent: true }); };
    const timer = setInterval(refresh, refreshMs);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [load, enabled, refreshMs]);

  return { data, loading, error, reload: load, setData };
}

const normalise = (name) => name.toLowerCase().replace(/[^a-z0-9]/g, '');

// Live scores refresh every 30s. `scoreFor` finds the live entry for a prediction,
// by ESPN id when the prediction has one, otherwise by (fuzzy) team names.
export function useLiveScores(intervalMs = 30000) {
  const [matches, setMatches] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const response = await fetch(`${getApiUrl()}/api/livescores`);
      const data = await response.json();
      if (data.success) {
        setMatches(data.all_matches || []);
        setUpdatedAt(data.timestamp);
      }
    } catch (err) {
      console.error('Failed to fetch live scores:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    timer.current = setInterval(load, intervalMs);
    return () => clearInterval(timer.current);
  }, [load, intervalMs]);

  const scoreFor = useCallback((prediction) => {
    if (!prediction) return null;
    if (prediction.espn_id) {
      const byId = matches.find(m => m.id === `espn_${prediction.espn_id}`);
      if (byId) return byId;
    }
    const home = normalise(prediction.home_team);
    const away = normalise(prediction.away_team);
    return matches.find(m => {
      const mh = normalise(m.home_team);
      const ma = normalise(m.away_team);
      return (mh.includes(home) || home.includes(mh)) && (ma.includes(away) || away.includes(ma));
    }) || null;
  }, [matches]);

  return { matches, updatedAt, loading, reload: load, scoreFor };
}
